import { useEffect, useRef } from 'react';

class SnowfallBackgroundStore {
  canvasRef = null;

  use() {
    this.canvasRef = useRef(null);

    useEffect(() => {
      const canvas = this.canvasRef && this.canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

      const state = {
        rafId: 0,
        width: 0,
        height: 0,
        flakes: [],
        lastT: 0
      };

      const rand = (min, max) => min + Math.random() * (max - min);

      const drawSnowflake = (x, y, size, rotation, strokeStyle, alpha) => {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rotation);
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = strokeStyle;
        ctx.lineWidth = Math.max(1, size / 5);
        ctx.lineCap = 'round';

        const arm = size;
        const branch = size * 0.45;
        const branchOffset = size * 0.45;
        const branchAngle = Math.PI / 6;

        ctx.beginPath();
        for (let k = 0; k < 6; k += 1) {
          const a = (Math.PI / 3) * k;
          const cos = Math.cos(a);
          const sin = Math.sin(a);

          ctx.moveTo(0, 0);
          ctx.lineTo(cos * arm, sin * arm);

          const bx = cos * branchOffset;
          const by = sin * branchOffset;
          const ca1 = Math.cos(a + branchAngle);
          const sa1 = Math.sin(a + branchAngle);
          const ca2 = Math.cos(a - branchAngle);
          const sa2 = Math.sin(a - branchAngle);

          ctx.moveTo(bx, by);
          ctx.lineTo(bx + ca1 * branch, by + sa1 * branch);
          ctx.moveTo(bx, by);
          ctx.lineTo(bx + ca2 * branch, by + sa2 * branch);
        }
        ctx.stroke();
        ctx.restore();
      };

      const resize = () => {
        const w = window.innerWidth;
        const h = window.innerHeight;
        state.width = w;
        state.height = h;
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const targetCount = Math.round(Math.min(220, Math.max(90, (w * h) / 18000)));
        if (state.flakes.length > targetCount) state.flakes.length = targetCount;
        while (state.flakes.length < targetCount) {
          const r = rand(1.0, 4.2);
          const mass = r * r;
          state.flakes.push({
            x: rand(0, w),
            y: rand(-h, h),
            r,
            mass,
            vy: rand(18, 52) + mass * 0.55,
            vx: rand(-8, 8),
            drift: rand(0.6, 2.0),
            phase: rand(0, Math.PI * 2),
            rot: rand(0, Math.PI * 2),
            vr: rand(-0.9, 0.9),
            seed: rand(0, 9999),
            bornAt: (state.lastT || 0) + rand(0, 800),
            blur: Math.random() < 0.22,
            alpha: rand(0.35, 0.9),
            tint: Math.random() < 0.08 ? 'gold' : 'white'
          });
        }
      };

      const draw = t => {
        const w = state.width;
        const h = state.height;
        if (!state.lastT) state.lastT = t;
        const dt = Math.min(0.05, Math.max(0.001, (t - state.lastT) / 1000));
        state.lastT = t;

        ctx.clearRect(0, 0, w, h);

        const windBase = Math.sin(t / 4200) * 10;
        const gust = Math.sin(t / 1600) * Math.sin(t / 3100) * 18;
        const wind = windBase + gust;

        for (let i = 0; i < state.flakes.length; i += 1) {
          const f = state.flakes[i];
          const sway = Math.sin((t / 1000) * f.drift + f.phase) * (4 + f.r * 1.1);
          const flutter = Math.sin((t / 1000) * (1.6 + f.r * 0.15) + f.seed) * (2.2 - Math.min(1.6, f.r * 0.35));
          const driftX = (wind + sway + flutter) * dt;

          f.x += (f.vx * dt) + driftX;
          f.y += f.vy * dt;
          f.rot += (f.vr * dt) * (0.35 + f.r * 0.12);

          if (f.y - f.r > h) {
            f.y = -f.r - rand(0, h * 0.15);
            f.x = rand(0, w);
            f.bornAt = t;
          }

          if (f.x < -10) f.x = w + 10;
          if (f.x > w + 10) f.x = -10;

          const strokeStyle = 'rgba(255, 255, 255, 1)';

          const age = Math.max(0, (t - (f.bornAt || 0)) / 1000);
          const fadeIn = Math.min(1, age / 0.9);
          const fadeOut = f.y > h * 0.92 ? Math.max(0, 1 - (f.y - h * 0.92) / (h * 0.08)) : 1;
          const alpha = (f.tint === 'gold' ? f.alpha * 0.5 : f.alpha) * fadeIn * fadeOut;

          if (f.r < 2) {
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.fillStyle = strokeStyle;
            ctx.beginPath();
            ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
          } else {
            if (f.blur) {
              ctx.save();
              ctx.shadowColor = strokeStyle;
              ctx.shadowBlur = Math.min(10, 1.5 + f.r * 1.4);
              drawSnowflake(f.x, f.y, f.r * 3.0, f.rot, strokeStyle, alpha * 0.95);
              ctx.restore();
            } else {
            drawSnowflake(f.x, f.y, f.r * 3.2, f.rot, strokeStyle, alpha);
            }
          }
        }

        state.rafId = window.requestAnimationFrame(draw);
      };

      resize();
      window.addEventListener('resize', resize, { passive: true });
      state.rafId = window.requestAnimationFrame(draw);

      return () => {
        window.removeEventListener('resize', resize);
        if (state.rafId) window.cancelAnimationFrame(state.rafId);
      };
    }, []);
  }
}

export default SnowfallBackgroundStore;
